/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import { providerUtil } from "dc-core";

export const MOBILE_SMALL = "(max-width: 294px)";
export const MOBILE = "(min-width: 295px) and (max-width: 767px)";
export const TABLET = "(min-width: 768px) and (max-width: 1279px)";
export const DESKTOP = "(min-width: 1280px) and (max-width: 1767px)";
export const LARGE_DESKTOP = "(min-width: 1768px)";

/**
 * Browsers which don't support FORCED_COLORS_ACTIVE media query,
 * 'NONE' will be returned in response
 */
export const FORCED_COLORS_ACTIVE = "(forced-colors: active)";
export const FC_ACTIVE = "ACTIVE";
export const FC_NONE = "NONE";

export const SUPPORTED_DEVICES = {
  MOBILE_SMALL,
  MOBILE,
  TABLET,
  DESKTOP,
  LARGE_DESKTOP
};

export const MEDIA_QUERY_TYPE = Object.freeze({
  DEVICE_WIDTH: "DEVICE_WIDTH",
  FORCED_COLORS: "FORCED_COLORS",
  ALL: "ALL"
});

/**
 * MediaQueryProvider allows using css media queries in js.
 * It also notifies listeners when the media query changes
 */
export default class MediaQueryProvider {
  static getInstance = providerUtil.getSingletonFunction(MediaQueryProvider);

  constructor() {
    this.listeners = {};
    this.mediaQueries = {};
    this.__init();
  }

  ready = () => Promise.resolve(this);

  /**
   * Get the active page breakpoint based on the current matched media query
   *
   * @param {MEDIA_QUERY_TYPE} mediaQueryType - type of media query,
   *  default value is MEDIA_QUERY_TYPE.DEVICE_WIDTH
   * @returns {string | null} - Value of the media query or null if not matched media query
   */
  getBreakpoint = (mediaQueryType = MEDIA_QUERY_TYPE.DEVICE_WIDTH) =>
    this.__getMatchedMediaQuery(mediaQueryType);

  /**
   * Adds a listener to be called when the media query changes
   *
   * @param {function} callback - the listener to add
   * @param {MEDIA_QUERY_TYPE} mediaQueryType - type of media query,
   *  default value is MEDIA_QUERY_TYPE.DEVICE_WIDTH
   */
  addChangeListener = (
    callback,
    mediaQueryType = MEDIA_QUERY_TYPE.DEVICE_WIDTH
  ) => {
    this.listeners[mediaQueryType] = this.listeners[mediaQueryType] || [];
    this.listeners[mediaQueryType].push(callback);
  };

  /**
   * Removes a listener
   *
   * @param {function} callback - the listener to remove
   * @param {MEDIA_QUERY_TYPE} mediaQueryType - type of media query,
   *  default value is MEDIA_QUERY_TYPE.DEVICE_WIDTH
   */
  removeChangeListener = (
    callback,
    mediaQueryType = MEDIA_QUERY_TYPE.DEVICE_WIDTH
  ) => {
    if (this.listeners[mediaQueryType]) {
      this.listeners[mediaQueryType] = this.listeners[mediaQueryType].filter(
        listener => listener !== callback
      );
    }
  };

  __init = () => {
    if (!window.matchMedia) {
      return;
    }

    this.mediaQueries[MEDIA_QUERY_TYPE.DEVICE_WIDTH] = Object.values(
      SUPPORTED_DEVICES
    ).map(mq => window.matchMedia(mq));
    this.mediaQueries[MEDIA_QUERY_TYPE.DEVICE_WIDTH].forEach(mediaQuery => {
      mediaQuery.addListener(() => {
        this.__changeListener(MEDIA_QUERY_TYPE.DEVICE_WIDTH);
      });
    });

    this.mediaQueries[MEDIA_QUERY_TYPE.FORCED_COLORS] = window.matchMedia(
      FORCED_COLORS_ACTIVE
    );
    this.mediaQueries[MEDIA_QUERY_TYPE.FORCED_COLORS].addListener(() => {
      this.__changeListener(MEDIA_QUERY_TYPE.FORCED_COLORS);
    });
  };

  __changeListener = mediaQueryType => {
    const newState = this.__getMatchedMediaQuery(mediaQueryType);
    this.__callbackListeners(
      this.listeners[mediaQueryType],
      newState,
      mediaQueryType
    );
    this.__callbackListeners(
      this.listeners[MEDIA_QUERY_TYPE.ALL],
      newState,
      mediaQueryType
    );
  };

  __getMatchedMediaQuery = mediaQueryType => {
    switch (mediaQueryType) {
      case MEDIA_QUERY_TYPE.FORCED_COLORS: {
        if (this.mediaQueries[mediaQueryType].matches) {
          return FC_ACTIVE;
        }
        return FC_NONE;
      }

      case MEDIA_QUERY_TYPE.DEVICE_WIDTH: {
        const index = this.mediaQueries[mediaQueryType].findIndex(
          mq => mq.matches
        );
        const supportedDevices = Object.keys(SUPPORTED_DEVICES);
        if (index < 0 || index >= supportedDevices.length) {
          return null;
        }
        return supportedDevices[index];
      }

      default:
        return null;
    }
  };

  __callbackListeners(listeners, newState, mediaQueryType) {
    if (listeners) {
      listeners.forEach(listener => {
        if (listener && typeof listener === "function") {
          listener(newState, mediaQueryType);
        }
      });
    }
  }
}
